/**
 * How fast a phase is actually moving, and how long is left.
 *
 * A bar answers "is this alive". It does not answer the question that follows on a throttled uplink:
 * *is it worth waiting for*. A 49 MB APK left a build machine at ~24 KB/s — sustained, with a stall in
 * the middle — and from the bar alone that is indistinguishable from a fast link on a big file. Thirty
 * minutes of waiting versus thirty seconds is the difference between watching and going away.
 *
 * Measured from the progress we already receive, never declared: the hub's numbers are bytes handed to
 * the socket, so a rate computed from them describes the link rather than our hopes for it.
 */

export interface Sample {
  done: number
  /** Milliseconds, from the browser's clock — only it knows when the number actually arrived. */
  at: number
}

export interface Rate {
  bytesPerSec: number | null
  etaSeconds: number | null
}

/** Samples older than this are dropped, so a rate follows a link that changes rather than averaging
 *  away a throttle that started a minute ago. */
export const WINDOW_MS = 60_000
/** Below this span the answer is noise: two polls a second apart divide by a rounding error. */
const MIN_SPAN_MS = 4_000

export function keepWindow(samples: Sample[], now: number): Sample[] {
  return samples.filter((s) => now - s.at <= WINDOW_MS)
}

export function rateFrom(samples: Sample[], total: number): Rate {
  const first = samples[0]
  const last = samples[samples.length - 1]
  if (!first || !last || first === last) return { bytesPerSec: null, etaSeconds: null }
  const span = last.at - first.at
  const moved = last.done - first.done
  // A stall reports zero, not "unknown": zero is the true rate of a transfer that is not moving, and
  // hiding it would leave the readout looking healthy at the moment it stops being true. But no ETA —
  // dividing by zero gives Infinity, and "∞ left" is a joke rather than an estimate.
  if (span < MIN_SPAN_MS || moved < 0) return { bytesPerSec: null, etaSeconds: null }
  const bytesPerSec = (moved / span) * 1000
  if (bytesPerSec <= 0) return { bytesPerSec: 0, etaSeconds: null }
  const remaining = total - last.done
  return {
    bytesPerSec,
    etaSeconds: remaining > 0 ? Math.round(remaining / bytesPerSec) : 0,
  }
}

export function formatRate(bytesPerSec: number): string {
  if (bytesPerSec < 1_000) return `${Math.round(bytesPerSec)} B/s`
  if (bytesPerSec < 1_000_000) return `${Math.round(bytesPerSec / 1_000)} KB/s`
  return `${(bytesPerSec / 1_000_000).toFixed(1)} MB/s`
}

/** `40s` / `12 min` / `1 h 05 min` — coarser as it gets longer, because nobody plans around the seconds
 *  of a half-hour wait, and a number that precise invites disbelief when it moves. */
export function formatEta(seconds: number): string {
  if (seconds < 90) return `${Math.max(1, Math.round(seconds))}s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`
}
